/* 15-api-contract.md section 1.6: the API is metric throughout and never
 * returns both units - "conversion for display is the client's job, done
 * once in a formatter." This is that formatter. No component converts a
 * measurement inline; everything below is a component's only path to a
 * display string.
 */
import type { Money } from "../api/types";

const METERS_PER_FOOT = 0.3048;
const METERS_PER_MILE = 1609.344;

export function metersToFeet(m: number): number {
  return m / METERS_PER_FOOT;
}

export function feetToMeters(ft: number): number {
  return ft * METERS_PER_FOOT;
}

export function metersToMiles(m: number): number {
  return m / METERS_PER_MILE;
}

/* A distance in feet with no unit, for a column whose header carries it; "—"
 * where none was measured, as formatDate reads a null. */
export function feetFigure(m: number | null, digits = 1): string {
  return m === null ? "—" : metersToFeet(m).toFixed(digits);
}

export function formatFeet(m: number | null, digits = 1): string {
  return m === null ? "—" : `${feetFigure(m, digits)} ft`;
}

/* Volume in cubic feet, as Kanopy's span pages state pruning volume. */
export function cubicMetersToCubicFeet(m3: number): number {
  return m3 / METERS_PER_FOOT ** 3;
}

/* A volume in cubic feet with no unit, as feetFigure is for a distance. */
export function cubicFeetFigure(m3: number | null, digits = 1): string {
  return m3 === null ? "—" : cubicMetersToCubicFeet(m3).toFixed(digits);
}

export function formatCubicFeet(m3: number | null, digits = 1): string {
  return m3 === null ? "—" : `${cubicFeetFigure(m3, digits)} ft³`;
}

export function formatMiles(m: number, digits = 1): string {
  return `${metersToMiles(m).toFixed(digits)} mi`;
}

export function formatGrowthRate(cmPerYear: number, digits = 1): string {
  // cm/yr -> ft/yr, the unit every growth-rate figure in the deck uses.
  return `${(cmPerYear / 100 / METERS_PER_FOOT).toFixed(digits)} ft/yr`;
}

export function formatPercent(fraction: number, digits = 1): string {
  return `${(fraction * 100).toFixed(digits)}%`;
}

/* "1 month", "2 months" - for a word whose plural is its s-form. */
export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

export function formatCount(n: number): string {
  return n.toLocaleString("en-US");
}

/* Money is an integer of cents with an explicit currency (15 section 1.6),
 * never a float - so this is the one place a cents integer becomes a
 * display string, and it never round-trips back into arithmetic. */
export function formatMoney(money: Money, opts?: { compact?: boolean }): string {
  const amount = money.cents / 100;
  if (opts?.compact) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: money.currency,
      notation: "compact",
      maximumFractionDigits: 2,
    }).format(amount);
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: money.currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

/* "en-GB" is a formatting choice, not a locale statement - it's the locale
 * that renders day-month-year with no comma ("14 Mar 2026"), which is the
 * date shape every screen in the deck uses. */
export function formatDate(iso: string | null, opts?: { withYear?: boolean }): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: opts?.withYear === false ? undefined : "numeric",
  });
}

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

export function formatDateTime(iso: string): string {
  return `${formatDate(iso)}, ${formatTime(iso)}`;
}

/* A record's handle on screen: the end of its id, uppercased - short enough
 * to read aloud and quote, which a UUID is not. It is not guaranteed unique;
 * the id in the URL is the identity. */
export function shortId(id: string): string {
  return id.slice(-5).toUpperCase();
}

/* A Kanopy id - a tree's or a pole's - as Kanopy's own tables show it: its
 * first 8 characters, so a record can be found by the same handle in both.
 * Not guaranteed unique; a row links by our id. */
export function kanopyHandle(kanopyId: string): string {
  return kanopyId.slice(0, 8);
}

/* A span by its end poles, as Kanopy names it: Kanopy has no span id, and the
 * pole pair is a span's key (`02` -> Kanopy's band is shown under Kanopy's
 * name). */
export function spanName(fromKanopyPoleId: string, toKanopyPoleId: string): string {
  return `${kanopyHandle(fromKanopyPoleId)} → ${kanopyHandle(toKanopyPoleId)}`;
}

/* Kanopy's job status as Kanopy's own pages show it - `COMPLETED` reads
 * "Completed" - its value otherwise as sent (07 section 6). */
export function kanopyStatus(status: string | null): string {
  if (status === null || status === "") return "—";
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase().replace(/_/g, " ");
}

/* Kanopy's metric accuracy, a percentage as Kanopy sends it (12.8 is 12.8%),
 * under Kanopy's name - never shown as coverage. */
export function metricAccuracy(pct: number | null): string {
  return pct === null ? "—" : formatPercent(pct / 100);
}

/* A capture as Kanopy's jobs list names it - its flight's location - or, where
 * the flight carries none, its Kanopy job handle. */
export function captureName(capture: { flight_location_title: string | null; kanopy_job_id: string }): string {
  return capture.flight_location_title ?? kanopyHandle(capture.kanopy_job_id);
}

/* A point as latitude, longitude to 5 decimal places - about a metre. */
export function formatCoordinates({ lat, lon }: { lat: number; lon: number }): string {
  return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
}

export function treeHandle(id: string): string {
  return `T-${shortId(id)}`;
}

/** MSAL's AccountInfo.name is optional and its own docs don't guarantee a
 * "First Last" shape, so this falls back to username (the UPN/email) rather
 * than assuming name is ever present. */
export function initialsFor(account: { name?: string; username: string }): string {
  const name = account.name?.trim();
  if (name) {
    const parts = name.split(/\s+/);
    return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : parts[0].slice(0, 2).toUpperCase();
  }
  return account.username.slice(0, 2).toUpperCase();
}
